import React, { useCallback, useEffect, useRef, useState } from 'react';
import Sidebar from '../../../components/Sidebar';
import AppDashboardNav from '../AppDashboardNav';
import {
  HiMenu,
  HiSearch,
  HiPlus,
  HiChevronLeft,
  HiChevronRight,
  HiChevronDown,
  HiInformationCircle,
  HiVideoCamera,
} from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import Toast from '../../../components/toaster';
import { uploadFile } from '../../../services/assetService';
import { fetchModuleList } from '../../../services/moduleService';
import { fetchChapterList } from '../../../services/chapterService';
import { fetchLessonList } from '../../../services/lessonContentService';
import {
  getMeditationSession,
  listMeditationSessions,
} from '../../../services/meditationService';
import {
  fetchFeaturedCardList,
  createFeaturedCard,
  updateFeaturedCard,
  deactivateFeaturedCard,
  reorderFeaturedCards,
} from '../../../services/featuredCardService';
import {
  FEATURED_ACTION_OPTIONS,
  FEATURED_MEDIA_TYPES,
  emptyFeaturedForm,
  normalizeFeaturedActionRef,
  sequentialFeaturedOrders,
  validateFeaturedForm,
} from '../../../constants/featuredActionTypes';
import EditOrderIcon from '../../../assets/icons/edit-order.svg';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PAGE_SIZE = 50;

const StatusBadge = ({ isActive }) => (
  <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${isActive ? 'bg-[#9FF7C9]/30 text-[#60BC7C]' : 'bg-[#FFF0F0] text-[#F76464]'}`}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="40" stroke="#A855F7" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" />
      </svg>
    </div>
  </div>
);

const GridIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" className="text-gray-400">
    <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
    <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
    <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
  </svg>
);

const SortableReorderItem = ({ id, title, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
    >
      <div className="flex items-center gap-4 min-w-0">
        <span className="text-sm font-semibold min-w-[20px]">{index + 1}</span>
        <p className="text-black font-medium truncate">{title}</p>
      </div>
      <GridIcon />
    </div>
  );
};

const actionLabel = (value) => FEATURED_ACTION_OPTIONS.find((row) => row.value === value)?.label || value;

const HTTP_URL_RE = /^https?:\/\//i;

const inferFeaturedMediaType = (file) => {
  const name = String(file?.name || '').toLowerCase();
  const type = String(file?.type || '').toLowerCase();
  if (type.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/i.test(name)) return 'video';
  if (type === 'image/gif' || name.endsWith('.gif')) return 'gif';
  return 'image';
};

const mediaAcceptForType = (mediaType) => {
  if (mediaType === 'video') return 'video/*';
  if (mediaType === 'gif') return 'image/gif,.gif';
  return 'image/*';
};

const isFeaturedVideoMedia = (item) =>
  String(item?.media_type || '').toLowerCase() === 'video' ||
  /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(String(item?.media_url || ''));

const captureVideoFrame = (source) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const isBlob = source instanceof Blob;
    const objectUrl = isBlob ? URL.createObjectURL(source) : null;
    if (!isBlob) video.crossOrigin = 'anonymous';
    let settled = false;
    let timer = null;
    const cleanup = () => {
      if (timer) window.clearTimeout(timer);
      video.onloadeddata = null;
      video.onseeked = null;
      video.onerror = null;
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    const fail = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error('Could not capture thumbnail from video'));
    };
    const grab = () => {
      try {
        if (!video.videoWidth || !video.videoHeight) {
          fail(new Error('Video has no frame to capture'));
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              fail(new Error('Could not capture thumbnail from video'));
              return;
            }
            if (settled) return;
            settled = true;
            cleanup();
            resolve(new File([blob], `featured-thumb-${Date.now()}.jpg`, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.86
        );
      } catch (err) {
        fail(err);
      }
    };
    timer = window.setTimeout(() => fail(new Error('Thumbnail capture timed out')), 12000);
    video.onerror = () => fail(new Error('Could not read video for thumbnail'));
    video.onloadeddata = () => {
      const duration = Number(video.duration);
      const target = Number.isFinite(duration) && duration > 1 ? Math.min(0.8, duration / 4) : 0.1;
      video.onseeked = grab;
      try {
        video.currentTime = target;
      } catch {
        grab();
      }
    };
    video.src = objectUrl || String(source);
    video.load();
  });

/** Sessions-style dropdown — not native system <select>. */
const CircularProgress = ({ className = 'h-4 w-4 text-brand' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-20" />
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeDasharray="42 56"
    />
  </svg>
);

const extractChapterRows = (chapterRes) => {
  if (Array.isArray(chapterRes?.data)) return chapterRes.data;
  if (Array.isArray(chapterRes?.data?.data)) return chapterRes.data.data;
  if (Array.isArray(chapterRes)) return chapterRes;
  if (Array.isArray(chapterRes?.chapters)) return chapterRes.chapters;
  if (Array.isArray(chapterRes?.results)) return chapterRes.results;
  return [];
};

const idOf = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

const chapterBelongsToModule = (chapter, moduleId) => {
  const chapterModuleId =
    idOf(chapter?.module_id) ||
    idOf(chapter?.moduleId) ||
    idOf(chapter?.module?.module_id) ||
    idOf(chapter?.module);
  return chapterModuleId === String(moduleId);
};

const FormSelect = ({
  id,
  label,
  required,
  value,
  disabled,
  loading = false,
  error,
  onChange,
  placeholder = 'Select…',
  options = [],
  dropUp = false,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const normalized = options.map((opt) =>
    typeof opt === 'object'
      ? { value: String(opt.value), label: opt.label }
      : { value: String(opt), label: String(opt) }
  );
  const selected = normalized.find((opt) => opt.value === String(value || ''));
  const display = selected ? selected.label : placeholder;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (disabled || loading) setOpen(false);
  }, [disabled, loading]);

  return (
    <div ref={rootRef} className="featured-form-dropdown">
      {label ? (
        <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => {
            if (loading) return;
            setOpen((prev) => !prev);
          }}
          className={`w-full h-10 pl-3 pr-10 text-sm text-left leading-normal border rounded-xl focus:outline-none bg-white truncate disabled:opacity-50 disabled:cursor-not-allowed ${
            error ? 'border-red-300' : 'border-[#ECECEC]'
          } ${!selected ? 'text-gray-400' : 'text-gray-900'}`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {display}
        </button>
        {loading ? (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            <CircularProgress />
          </span>
        ) : (
          <HiChevronDown
            className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
        )}
        {open ? (
          <div
            className={`absolute z-[160] left-0 right-0 max-h-60 overflow-y-auto bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200 ${
              dropUp ? 'bottom-full mb-1' : 'mt-1'
            }`}
            role="listbox"
            aria-labelledby={id}
          >
            {normalized.map((opt) => {
              const isSelected = String(value || '') === opt.value;
              return (
                <button
                  key={opt.value || opt.label}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 truncate ${
                    isSelected ? 'bg-gray-50 font-semibold' : 'text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
};

const SESSION_PICKER_PAGE_SIZE = 20;

const extractSessionList = (res) => {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

/** Search + paginated Dhyaan session picker for Featured (many sessions). */
const FeaturedSessionSelect = ({
  id = 'featured-dhyaan-session',
  label = 'Dhyaan session',
  value,
  error,
  disabled,
  dropUp = false,
  placeholder = 'None (next unlocked)',
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const rootRef = useRef(null);
  const searchTimerRef = useRef(null);
  const requestIdRef = useRef(0);
  const selectedId = String(value || '').trim();
  const hasMore = rows.length < totalCount;

  const display = selectedId
    ? selectedLabel || (rows.find((row) => String(row._id) === selectedId)?.title) || 'Selected session'
    : placeholder;

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    let cancelled = false;
    const hydrateSelected = async () => {
      if (!selectedId) {
        setSelectedLabel('');
        return;
      }
      const fromRows = rows.find((row) => String(row._id) === selectedId);
      if (fromRows?.title) {
        setSelectedLabel(fromRows.title);
        return;
      }
      try {
        const res = await getMeditationSession(selectedId);
        if (cancelled) return;
        const session = res?.data || res;
        setSelectedLabel(session?.title || 'Selected session');
      } catch {
        if (!cancelled) setSelectedLabel('Selected session');
      }
    };
    hydrateSelected();
    return () => {
      cancelled = true;
    };
    // rows intentionally omitted — only re-hydrate when selection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const loadPage = useCallback(async (nextPage, searchText, append) => {
    const reqId = ++requestIdRef.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const body = {
        pagination: { page: nextPage, pageSize: SESSION_PICKER_PAGE_SIZE },
      };
      if (searchText) body.searchtext = searchText;
      const res = await listMeditationSessions(body);
      if (reqId !== requestIdRef.current) return;
      const nextRows = extractSessionList(res);
      const total = typeof res?.totalCount === 'number' ? res.totalCount : nextRows.length;
      setTotalCount(total);
      setPage(nextPage);
      setRows((prev) => (append ? [...prev, ...nextRows] : nextRows));
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      if (!append) {
        setRows([]);
        setTotalCount(0);
      }
      Toast.error(err.message || 'Failed to load Dhyaan sessions');
    } finally {
      if (reqId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadPage(1, debouncedSearch, false);
  }, [open, debouncedSearch, loadPage]);

  const pick = (nextId, nextLabel = '') => {
    onChange(nextId);
    setSelectedLabel(nextId ? nextLabel : '');
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="featured-form-dropdown">
      {label ? (
        <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">
          {label}
        </label>
      ) : null}
      <div className="relative">
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          className={`w-full h-10 pl-3 pr-10 text-sm text-left leading-normal border rounded-xl focus:outline-none bg-white truncate disabled:opacity-50 disabled:cursor-not-allowed ${
            error ? 'border-red-300' : 'border-[#ECECEC]'
          } ${!selectedId ? 'text-gray-400' : 'text-gray-900'}`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {display}
        </button>
        <HiChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
        {open ? (
          <div
            className={`absolute z-[160] left-0 right-0 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200 ${
              dropUp ? 'bottom-full mb-1' : 'mt-1'
            }`}
          >
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <HiSearch className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  autoFocus
                  placeholder="Search sessions…"
                  onChange={(event) => setSearch(event.target.value)}
                  className="w-full h-9 pl-8 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                />
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto" role="listbox" aria-labelledby={id}>
              <button
                type="button"
                role="option"
                aria-selected={!selectedId}
                onClick={() => pick('')}
                className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 truncate ${
                  !selectedId ? 'bg-gray-50 font-semibold' : 'text-gray-900'
                }`}
              >
                {placeholder}
              </button>
              {loading ? (
                <div className="px-3 py-3 flex items-center gap-2 text-xs text-gray-500">
                  <CircularProgress />
                  Loading sessions…
                </div>
              ) : null}
              {!loading && rows.length === 0 ? (
                <p className="px-3 py-3 text-xs text-gray-500">No sessions found.</p>
              ) : null}
              {!loading
                ? rows.map((session) => {
                    const sid = String(session._id || '');
                    const title = session.title || 'Untitled session';
                    const isSelected = selectedId === sid;
                    return (
                      <button
                        key={sid}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => pick(sid, title)}
                        className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 truncate ${
                          isSelected ? 'bg-gray-50 font-semibold' : 'text-gray-900'
                        }`}
                      >
                        {title}
                      </button>
                    );
                  })
                : null}
              {!loading && hasMore ? (
                <div className="p-2 border-t border-gray-100">
                  <button
                    type="button"
                    disabled={loadingMore}
                    onClick={() => loadPage(page + 1, debouncedSearch, true)}
                    className="w-full h-8 text-xs font-medium text-brand border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading…' : `Load more (${rows.length}/${totalCount})`}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
};

/** Module/Sessions media box: URL/file toggle, image/video/gif upload + preview. */
const FeaturedMediaField = ({
  mediaUrl,
  mediaType,
  error,
  disabled,
  resetKey,
  onUrlChange,
  onTypeChange,
  onThumbnailChange,
  onCapturingThumbnailChange,
}) => {
  const [mediaSource, setMediaSource] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);
  const lastThumbSourceRef = useRef('');
  const trimmed = String(mediaUrl || '').trim();
  const canPreview = Boolean(trimmed) && HTTP_URL_RE.test(trimmed) && !error && !uploadError;

  const grabThumbnail = async (source, sourceKey) => {
    if (!onThumbnailChange) return;
    onCapturingThumbnailChange?.(true);
    try {
      const file = await captureVideoFrame(source);
      const thumbUrl = await uploadFile(file);
      lastThumbSourceRef.current = sourceKey;
      onThumbnailChange(thumbUrl);
    } catch (err) {
      lastThumbSourceRef.current = '';
      onThumbnailChange('');
      setUploadError(err.message || 'Could not capture thumbnail from video.');
    } finally {
      onCapturingThumbnailChange?.(false);
    }
  };

  useEffect(() => {
    setMediaSource('url');
    setUploading(false);
    setUploadError('');
    setPreviewFailed(false);
    lastThumbSourceRef.current = trimmed;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed, mediaType]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setMediaSource(next);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || uploading || disabled) return;
    const nextType = inferFeaturedMediaType(file);
    setUploading(true);
    setUploadError('');
    try {
      const uploadedUrl = await uploadFile(file);
      if (nextType === 'video') lastThumbSourceRef.current = uploadedUrl;
      else lastThumbSourceRef.current = '';
      onTypeChange(nextType);
      onUrlChange(uploadedUrl);
      if (nextType === 'video') await grabThumbnail(file, uploadedUrl);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload media. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled) return;
    setUploadError('');
    setPreviewFailed(false);
    lastThumbSourceRef.current = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
    onUrlChange('');
    onThumbnailChange?.('');
  };

  useEffect(() => {
    if (mediaType !== 'video' || uploading || !HTTP_URL_RE.test(trimmed)) return undefined;
    if (lastThumbSourceRef.current === trimmed) return undefined;
    const timer = window.setTimeout(() => {
      grabThumbnail(trimmed, trimmed);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [trimmed, mediaType, uploading]);

  const showClear = Boolean(trimmed) || Boolean(uploadError);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-gray-700">
          Media <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[60] sm:whitespace-nowrap leading-tight inline-block ${
                mediaSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {mediaSource === 'url'
                ? 'Switch left to choose a file from your device'
                : 'Switch right to paste a media URL.'}
            </span>
          </div>
          <button
            type="button"
            aria-label={mediaSource === 'url' ? 'Switch to file upload' : 'Switch to media URL'}
            disabled={uploading || disabled}
            onClick={() => switchSource(mediaSource === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              mediaSource === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                mediaSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="mb-2">
        <FormSelect
          id="featured-media-type"
          value={mediaType}
          disabled={disabled || uploading}
          onChange={onTypeChange}
          options={FEATURED_MEDIA_TYPES.map((type) => ({
            value: type,
            label: type === 'gif' ? 'GIF' : type.charAt(0).toUpperCase() + type.slice(1),
          }))}
        />
      </div>

      {mediaSource === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              type="url"
              value={mediaUrl}
              disabled={uploading || disabled}
              onChange={(event) => {
                setUploadError('');
                onUrlChange(event.target.value);
              }}
              placeholder="https://…"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                error || uploadError ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">HTTP/HTTPS URL for image, video, or GIF.</p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={mediaAcceptForType(mediaType)}
              disabled={uploading || disabled}
              onChange={handleFileSelect}
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                uploadError || error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">Uploading media...</span>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">
            {mediaType === 'video'
              ? 'Upload a video file. Type is set from the file.'
              : mediaType === 'gif'
                ? 'Upload a GIF. Type is set from the file.'
                : 'Upload an image. GIF/video files switch type automatically.'}
          </p>
        </div>
      )}

      {uploadError ? <p className="mt-1 text-xs text-red-600">{uploadError}</p> : null}
      {error && !uploadError ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}

      {canPreview && !previewFailed && !uploading && mediaType === 'video' && (
        <video
          src={trimmed}
          className="mt-2 w-full max-h-40 rounded-lg border border-gray-200 bg-black"
          muted
          controls
          loop
          onError={() => setPreviewFailed(true)}
        />
      )}
      {canPreview && !previewFailed && !uploading && mediaType !== 'video' && (
        <img
          src={trimmed}
          alt="Featured media preview"
          className="mt-2 h-20 w-20 object-cover rounded-lg border border-gray-200 bg-gray-50"
          onError={() => setPreviewFailed(true)}
        />
      )}
      {canPreview && previewFailed && !uploading && (
        <p className="mt-2 text-xs text-amber-700">Preview unavailable — URL may be broken.</p>
      )}
    </div>
  );
};

/** Poster for video Featured cards. Auto-filled from the video; disabled until a video exists. */
const FeaturedThumbnailField = ({
  thumbnailUrl,
  error,
  disabled,
  videoReady,
  capturing,
  resetKey,
  onUrlChange,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);
  const trimmed = String(thumbnailUrl || '').trim();
  const canPreview = Boolean(trimmed) && HTTP_URL_RE.test(trimmed) && !error && !uploadError;

  useEffect(() => {
    setUploading(false);
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed]);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || uploading || disabled || !videoReady) return;
    if (inferFeaturedMediaType(file) === 'video') {
      setUploadError('Upload an image for the thumbnail.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const uploadedUrl = await uploadFile(file);
      onUrlChange(uploadedUrl);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload thumbnail. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled || capturing || !videoReady) return;
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onUrlChange('');
  };

  const showClear = Boolean(trimmed) || Boolean(uploadError);

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        Thumbnail <span className="text-red-500">*</span>
      </label>
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          disabled={uploading || disabled || capturing || !videoReady}
          onChange={handleFileSelect}
          className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
            uploadError || error ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {showClear && (
          <button
            type="button"
            onClick={handleClear}
            disabled={uploading || disabled || capturing || !videoReady}
            className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
          >
            Clear
          </button>
        )}
      </div>
      {capturing ? (
        <p className="mt-1 text-xs text-gray-600">Taking thumbnail from video...</p>
      ) : uploading ? (
        <p className="mt-1 text-xs text-gray-600">Uploading thumbnail...</p>
      ) : (
        <p className="mt-1 text-xs text-gray-400">
          {videoReady
            ? 'Taken from the video automatically. Replace only if needed.'
            : 'Add a video first. Thumbnail stays disabled until then.'}
        </p>
      )}
      {uploadError ? <p className="mt-1 text-xs text-red-600">{uploadError}</p> : null}
      {error && !uploadError ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      {canPreview && !previewFailed && !uploading ? (
        <img
          src={trimmed}
          alt="Featured thumbnail preview"
          className="mt-2 h-20 w-20 object-cover rounded-lg border border-gray-200 bg-gray-50"
          onError={() => setPreviewFailed(true)}
        />
      ) : null}
      {canPreview && previewFailed && !uploading ? (
        <p className="mt-2 text-xs text-amber-700">Preview unavailable — URL may be broken.</p>
      ) : null}
    </div>
  );
};

const FeaturedCards = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(emptyFeaturedForm());
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [saving, setSaving] = useState(false);
  const [capturingThumbnail, setCapturingThumbnail] = useState(false);
  const [pickerModuleId, setPickerModuleId] = useState('');
  const [modules, setModules] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [itemToDeactivate, setItemToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderCards, setReorderCards] = useState([]);
  const [reorderSaving, setReorderSaving] = useState(false);
  const searchTimerRef = useRef(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 0;

  const loadData = useCallback(async (searchText = '', page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchFeaturedCardList(page, PAGE_SIZE, searchText);
      const items = Array.isArray(res?.data) ? res.data : [];
      setData(items);
      setTotalCount(res?.totalCount ?? items.length);
    } catch (err) {
      setError(err.message || 'Error loading featured cards.');
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    loadData(debouncedSearch, currentPage);
  }, [debouncedSearch, currentPage, loadData]);

  const loadPickers = async () => {
    try {
      const moduleRes = await fetchModuleList(1, 100, '', '');
      setModules(Array.isArray(moduleRes?.data) ? moduleRes.data : []);
    } catch (err) {
      Toast.error(err.message || 'Failed to load pickers');
    }
  };

  const loadChaptersForModule = async (moduleId) => {
    if (!moduleId) {
      setChapters([]);
      setChaptersLoading(false);
      return;
    }
    setChaptersLoading(true);
    try {
      const chapterRes = await fetchChapterList(moduleId, 1, 100, '', {});
      const rows = extractChapterRows(chapterRes);
      const filtered = rows.filter((chapter) => chapterBelongsToModule(chapter, moduleId));
      setChapters(filtered.length ? filtered : rows);
    } catch (err) {
      setChapters([]);
      Toast.error(err.message || 'Failed to load chapters');
    } finally {
      setChaptersLoading(false);
    }
  };

  const firstActiveChapterId = async () => {
    const moduleId = String(modules[0]?._id || '');
    if (!moduleId) return '';
    const chapterRes = await fetchChapterList(moduleId, 1, 100, '', {});
    return String(chapterRes?.data?.[0]?._id || '');
  };

  const bindFirstActiveLesson = async () => {
    const chapterId = await firstActiveChapterId();
    if (!chapterId) return '';
    const lessonRes = await fetchLessonList(chapterId, 1, 100, '', {});
    const firstId = String(lessonRes?.data?.[0]?._id || '');
    setFormData((prev) => ({ ...prev, lesson_id: firstId, chapter_id: '' }));
    return firstId;
  };

  const bindFirstActiveChapter = async () => {
    const firstId = await firstActiveChapterId();
    setFormData((prev) => ({ ...prev, chapter_id: firstId }));
    return firstId;
  };

  const openAdd = async () => {
    setFormData(emptyFeaturedForm());
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
    setPickerModuleId('');
    setChapters([]);
    setChaptersLoading(false);
    setShowFormModal(true);
    await loadPickers();
  };

  const hydratePickersFromCard = async (item) => {
    await loadPickers();
    const ref = item.action_ref || {};
    if ((item.action_type === 'contest_rules' || item.action_type === 'specific_module') && ref.module_id) {
      setPickerModuleId(ref.module_id);
      if (item.action_type === 'specific_module') await loadChaptersForModule(ref.module_id);
    }
  };

  const openEdit = async (item) => {
    const ref = item.action_ref || {};
    setFormData({
      ...emptyFeaturedForm(),
      media_url: item.media_url || '',
      media_type: item.media_type || 'image',
      thumbnail_url: item.thumbnail_url || '',
      title: item.title || '',
      description: item.description || '',
      tag_title: item.tag_title || '',
      button_title: item.button_title || '',
      button_color: item.button_color || '#58327E',
      button_border_color: item.button_border_color || '#58327E',
      button_text_color: item.button_text_color || '#FFFFFF',
      action_type: item.action_type || 'talk_to_krishna',
      lesson_id: ref.lesson_id || '',
      chapter_id: ref.chapter_id || '',
      session_id: ref.session_id || '',
      module_id: ref.module_id || '',
      url: ref.url || '',
      is_active: item.is_active !== false,
    });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(item);
    setIsEditing(true);
    setChaptersLoading(item.action_type === 'specific_module' && Boolean(ref.module_id));
    setShowFormModal(true);
    await hydratePickersFromCard(item);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setFormData(emptyFeaturedForm());
    setSelectedItem(null);
    setIsEditing(false);
    setCapturingThumbnail(false);
    setChapters([]);
    setChaptersLoading(false);
  };

  const handleSave = async () => {
    if (capturingThumbnail) return;
    let nextForm = formData;
    if (nextForm.action_type === 'lesson' && !nextForm.lesson_id) {
      const firstId = await bindFirstActiveLesson();
      nextForm = { ...nextForm, lesson_id: firstId };
      setFormData(nextForm);
    }
    if (nextForm.action_type === 'chapter' && !nextForm.chapter_id) {
      const firstId = await bindFirstActiveChapter();
      nextForm = { ...nextForm, chapter_id: firstId };
      setFormData(nextForm);
    }
    const errs = validateFeaturedForm(nextForm);
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }
    const payload = {
      media_url: nextForm.media_url.trim(),
      media_type: nextForm.media_type,
      thumbnail_url: nextForm.media_type === 'video' ? nextForm.thumbnail_url.trim() : '',
      title: nextForm.title.trim(),
      description: nextForm.description.trim(),
      tag_title: nextForm.tag_title.trim(),
      button_title: nextForm.button_title.trim(),
      button_color: nextForm.button_color,
      button_border_color: nextForm.button_border_color,
      button_text_color: nextForm.button_text_color,
      action_type: nextForm.action_type,
      action_ref: normalizeFeaturedActionRef(nextForm.action_type, nextForm),
      is_active: nextForm.is_active,
    };
    setSaving(true);
    setFormGeneralError('');
    try {
      if (isEditing) {
        await updateFeaturedCard({ featured_card_id: selectedItem._id, ...payload });
        Toast.success('Featured card updated');
      } else {
        await createFeaturedCard(payload);
        Toast.success('Featured card created');
      }
      closeForm();
      await loadData(debouncedSearch, isEditing ? currentPage : 1);
      if (!isEditing) setCurrentPage(1);
    } catch (err) {
      setFormGeneralError(err.message || 'Failed to save featured card');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivate = async () => {
    if (!itemToDeactivate) return;
    setDeactivating(true);
    try {
      await deactivateFeaturedCard(itemToDeactivate._id);
      Toast.success('Featured card deactivated');
      setShowDeactivateModal(false);
      setItemToDeactivate(null);
      await loadData(debouncedSearch, currentPage);
    } catch (err) {
      Toast.error(err.message || 'Failed to deactivate');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReorderDrag = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setReorderCards((items) => {
      const oldIndex = items.findIndex((row) => row._id === active.id);
      const newIndex = items.findIndex((row) => row._id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const saveReorder = async () => {
    setReorderSaving(true);
    try {
      await reorderFeaturedCards(sequentialFeaturedOrders(reorderCards));
      Toast.success('Featured order updated');
      setShowReorderModal(false);
      await loadData(debouncedSearch, currentPage);
    } catch (err) {
      Toast.error(err.message || 'Failed to save order');
    } finally {
      setReorderSaving(false);
    }
  };

  const needsModule = formData.action_type === 'contest_rules' || formData.action_type === 'specific_module';
  const mediaResetKey = showFormModal ? (isEditing ? selectedItem?._id || 'edit' : 'add') : '';

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/50">
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">App Dashboard</h1>
            </div>
          </div>
          <AppDashboardNav />

          <div className="mb-4 flex flex-col md:flex-row md:justify-between gap-3">
            <div className="relative w-full md:w-64">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search featured cards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setReorderCards(data.slice().sort((a, b) => (a.order || 0) - (b.order || 0)));
                  setShowReorderModal(true);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl bg-brand hover:opacity-90"
              >
                <img src={EditOrderIcon} alt="" className="w-4 h-4" style={{ filter: 'brightness(0) invert(1)' }} />
                Edit Order
              </button>
              <button onClick={openAdd} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl bg-brand hover:opacity-90">
                <HiPlus className="w-5 h-5" />
                Add Featured
              </button>
            </div>
          </div>

          {isLoading ? (
            <FullPageSpinner />
          ) : totalCount === 0 && !error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No featured cards yet</h3>
                <p className="text-sm text-gray-500 mb-6">Add the first carousel card for the new home.</p>
                <button onClick={openAdd} className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl bg-brand">
                  <HiPlus className="w-4 h-4" /> Add Featured
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              {error ? (
                <div className="px-4 py-6 text-center text-red-500">{error}</div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Media</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Title</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Action</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.map((item) => (
                        <tr key={item._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            {isFeaturedVideoMedia(item) && item.thumbnail_url ? (
                              <img src={item.thumbnail_url} alt="" className="w-12 h-10 rounded-lg object-cover bg-gray-100" />
                            ) : isFeaturedVideoMedia(item) ? (
                              <div className="w-12 h-10 rounded-lg bg-gray-100 flex items-center justify-center" title="Video">
                                <HiVideoCamera className="w-6 h-6 text-gray-500" />
                              </div>
                            ) : item.media_url ? (
                              <img src={item.media_url} alt="" className="w-12 h-10 rounded-lg object-cover bg-gray-100" />
                            ) : (
                              <div className="w-12 h-10 rounded-lg bg-gray-100" />
                            )}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800">{item.title}</td>
                          <td className="px-4 py-3 hidden md:table-cell text-gray-600">{actionLabel(item.action_type)}</td>
                          <td className="px-4 py-3"><StatusBadge isActive={item.is_active} /></td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg" style={{ backgroundColor: '#F2E2F7' }}>
                                <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                              <button
                                onClick={() => { setItemToDeactivate(item); setShowDeactivateModal(true); }}
                                className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg"
                                title="Deactivate"
                              >
                                <MdDelete className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {totalPages > 1 && (
                <div className="px-4 py-3 flex justify-end gap-1 border-t">
                  <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg disabled:opacity-40">
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg disabled:opacity-40">
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b font-bold">{isEditing ? 'Edit Featured card' : 'Add Featured card'}</div>
            <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1 text-sm">
              {formGeneralError && <div className="p-3 bg-red-50 text-red-700 rounded-lg">{formGeneralError}</div>}
              <label className="block text-xs font-medium">Title *</label>
              <input className="w-full border rounded-lg px-3 py-2" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
              {formErrors.title && <p className="text-xs text-red-600">{formErrors.title}</p>}
              <label className="block text-xs font-medium">Description *</label>
              <textarea className="w-full border rounded-lg px-3 py-2" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              <label className="block text-xs font-medium">Tag *</label>
              <input className="w-full border rounded-lg px-3 py-2" value={formData.tag_title} onChange={(e) => setFormData({ ...formData, tag_title: e.target.value })} />
              <label className="block text-xs font-medium">Button title *</label>
              <input className="w-full border rounded-lg px-3 py-2" value={formData.button_title} onChange={(e) => setFormData({ ...formData, button_title: e.target.value })} />
              <div className="grid grid-cols-3 gap-2">
                {['button_color', 'button_border_color', 'button_text_color'].map((key) => (
                  <div key={key}>
                    <label className="block text-xs font-medium mb-1">{key.replaceAll('_', ' ')}</label>
                    <input type="color" className="w-full h-9 border rounded" value={formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })} />
                    {formErrors[key] && <p className="text-xs text-red-600">{formErrors[key]}</p>}
                  </div>
                ))}
              </div>
              <FeaturedMediaField
                mediaUrl={formData.media_url}
                mediaType={formData.media_type}
                error={formErrors.media_url || formErrors.media_type}
                disabled={saving || capturingThumbnail}
                resetKey={mediaResetKey}
                onUrlChange={(url) => {
                  setFormData((prev) => ({
                    ...prev,
                    media_url: url,
                    thumbnail_url: url ? prev.thumbnail_url : '',
                  }));
                  if (formErrors.media_url) setFormErrors((prev) => ({ ...prev, media_url: '' }));
                }}
                onTypeChange={(type) => {
                  setFormData((prev) => ({
                    ...prev,
                    media_type: type,
                    thumbnail_url: type === 'video' ? prev.thumbnail_url : '',
                  }));
                  if (formErrors.media_type) setFormErrors((prev) => ({ ...prev, media_type: '' }));
                  if (type !== 'video' && formErrors.thumbnail_url) {
                    setFormErrors((prev) => ({ ...prev, thumbnail_url: '' }));
                  }
                }}
                onThumbnailChange={(url) => {
                  setFormData((prev) => ({ ...prev, thumbnail_url: url }));
                  if (url && formErrors.thumbnail_url) {
                    setFormErrors((prev) => ({ ...prev, thumbnail_url: '' }));
                  }
                }}
                onCapturingThumbnailChange={setCapturingThumbnail}
              />
              {formData.media_type === 'video' ? (
                <FeaturedThumbnailField
                  thumbnailUrl={formData.thumbnail_url}
                  error={formErrors.thumbnail_url}
                  disabled={saving}
                  videoReady={HTTP_URL_RE.test(String(formData.media_url || '').trim())}
                  capturing={capturingThumbnail}
                  resetKey={`${mediaResetKey}-thumb`}
                  onUrlChange={(url) => {
                    setFormData((prev) => ({ ...prev, thumbnail_url: url }));
                    if (formErrors.thumbnail_url) setFormErrors((prev) => ({ ...prev, thumbnail_url: '' }));
                  }}
                />
              ) : null}
              <FormSelect
                id="featured-action-type"
                label="Action"
                required
                value={formData.action_type}
                error={formErrors.action_type}
                onChange={(next) => {
                  setFormData((prev) => ({
                    ...prev,
                    action_type: next,
                    lesson_id: '',
                    chapter_id: '',
                    session_id: '',
                    module_id: '',
                    url: '',
                  }));
                  setPickerModuleId('');
                  setChapters([]);
                }}
                options={FEATURED_ACTION_OPTIONS}
              />
              {needsModule && (
                <FormSelect
                  id="featured-module"
                  label="Module"
                  required
                  value={pickerModuleId || formData.module_id}
                  error={formErrors.module_id}
                  placeholder="Select module"
                  onChange={async (id) => {
                    setPickerModuleId(id);
                    setFormData((prev) => ({ ...prev, module_id: id, chapter_id: '', lesson_id: '' }));
                    await loadChaptersForModule(id);
                  }}
                  options={[{ value: '', label: 'Select module' }, ...modules.map((mod) => ({ value: mod._id, label: mod.title }))]}
                />
              )}
              {formData.action_type === 'lesson' ? (
                <p className="text-xs text-gray-500">
                  App opens the next unlocked lesson. No specific module, chapter, or lesson pick.
                </p>
              ) : null}
              {formData.action_type === 'chapter' ? (
                <p className="text-xs text-gray-500">
                  App opens the next unlocked chapter. No specific module or chapter pick.
                </p>
              ) : null}
              {formData.action_type === 'dhyaan_session' ? (
                <>
                  <p className="text-xs text-gray-500">
                    Leave empty for next unlocked Dhyaan session. Pick one to always open that session.
                  </p>
                  <FeaturedSessionSelect
                    id="featured-dhyaan-session"
                    label="Dhyaan session"
                    dropUp
                    disabled={saving || capturingThumbnail}
                    value={formData.session_id}
                    error={formErrors.session_id}
                    placeholder="None (next unlocked)"
                    onChange={(id) => {
                      setFormData((prev) => ({ ...prev, session_id: id }));
                      if (formErrors.session_id) {
                        setFormErrors((prev) => ({ ...prev, session_id: '' }));
                      }
                    }}
                  />
                </>
              ) : null}
              {formData.action_type === 'specific_module' ? (
                <>
                  <p className="text-xs text-gray-500">
                    App opens this module. Chapter is optional and jumps to that chapter.
                  </p>
                  <FormSelect
                    id="featured-specific-chapter"
                    label="Chapter"
                    dropUp
                    loading={chaptersLoading}
                    value={formData.chapter_id}
                    disabled={!pickerModuleId && !formData.module_id}
                    placeholder="Optional — open module"
                    onChange={(id) => setFormData((prev) => ({ ...prev, chapter_id: id }))}
                    options={[
                      { value: '', label: 'None (open module)' },
                      ...chapters.map((chapter) => ({
                        value: chapter._id,
                        label: chapter.title || 'Untitled chapter',
                      })),
                    ]}
                  />
                </>
              ) : null}
              {formData.action_type === 'vedify_web' && (
                <input className="w-full border rounded-lg px-3 py-2" placeholder="https://any-url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} />
              )}
              <label className="flex items-center gap-2 text-xs font-medium">
                <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                Active
              </label>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button onClick={closeForm} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleSave} disabled={saving || capturingThumbnail} className="px-4 py-2 text-sm text-white rounded-lg bg-brand disabled:opacity-50">{capturingThumbnail ? 'Taking thumbnail…' : saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-gray-800 mb-2">Deactivate this card?</h3>
            <p className="text-sm text-gray-500 mb-4">It is hidden on the app. The row is kept — no hard delete.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDeactivateModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={confirmDeactivate} disabled={deactivating} className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg">Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {showReorderModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[80vh] flex flex-col">
            <h3 className="font-semibold mb-3">Reorder featured cards</h3>
            <div className="flex-1 overflow-y-auto space-y-2">
              <DndContext collisionDetection={closestCenter} onDragEnd={handleReorderDrag}>
                <SortableContext items={reorderCards.map((row) => row._id)} strategy={verticalListSortingStrategy}>
                  {reorderCards.map((row, index) => (
                    <SortableReorderItem key={row._id} id={row._id} title={row.title} index={index} />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowReorderModal(false)} disabled={reorderSaving} className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50">Cancel</button>
              <button
                onClick={saveReorder}
                disabled={reorderSaving}
                className="px-4 py-2 text-sm text-white bg-brand rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaving ? (
                  <>
                    <CircularProgress className="h-4 w-4 text-white" />
                    Saving…
                  </>
                ) : (
                  'Save order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedCards;
